import { SiteViewFeatureGenerationConfig } from 'features/overview'
import isEqual from 'lodash/isEqual'
import React, {
  createContext,
  Dispatch,
  FC,
  MouseEvent,
  ReactNode,
  SetStateAction,
  useCallback,
  useState,
} from 'react'
import { Vector2 } from 'three'

export enum MapFeatureType {
  Unit = 'unit',
  Unknown = 'unknown',
}

export type SelectedFeatureData =
  | {
      unitID: string
      type: MapFeatureType.Unit
    }
  | {
      featureID: string
      type: MapFeatureType.Unknown
    }
type FeatureInteractionContextType = {
  selectedFeature: SelectedFeatureData | undefined
  setSelectedFeature: Dispatch<SetStateAction<SelectedFeatureData | undefined>>

  showFeaturePopupMenu: Vector2 | undefined
  setShowFeaturePopupMenu: Dispatch<SetStateAction<Vector2 | undefined>>

  siteViewFeatureGenerationConfig: SiteViewFeatureGenerationConfig | undefined
  setSiteViewFeatureGenerationConfig: Dispatch<
    SetStateAction<SiteViewFeatureGenerationConfig | undefined>
  >

  onFeatureClick: (
    e: Pick<MouseEvent, 'clientX' | 'clientY' | 'stopPropagation'>,
    feature: SelectedFeatureData
  ) => void

  getHasDetailedModel: (unitID: string) => boolean
}

const FeatureInteractionContext = createContext<
  FeatureInteractionContextType | undefined
>(undefined)

export const FeatureInteractionContextProvider: FC<{
  children: ReactNode
  getHasDetailedModel: (unitID: string) => boolean
}> = ({ children, getHasDetailedModel }) => {
  const [showFeaturePopupMenu, setShowFeaturePopupMenu] = useState<Vector2>()
  const [selectedFeature, setSelectedFeature] = useState<SelectedFeatureData>()
  const [siteViewFeatureGenerationConfig, setSiteViewFeatureGenerationConfig] =
    useState<SiteViewFeatureGenerationConfig | undefined>(undefined)

  const onFeatureClick = useCallback<
    FeatureInteractionContextType['onFeatureClick']
  >(
    (e, feature) => {
      if (siteViewFeatureGenerationConfig) return
      e.stopPropagation()

      setSelectedFeature((prev) => {
        const isReSelected = isEqual(prev, feature)
        if (isReSelected) {
          setShowFeaturePopupMenu(undefined)
          return undefined
        } else {
          setShowFeaturePopupMenu(new Vector2(e.clientX, e.clientY))
          return feature
        }
      })
    },
    [siteViewFeatureGenerationConfig]
  )

  return (
    <FeatureInteractionContext.Provider
      value={{
        showFeaturePopupMenu,
        setShowFeaturePopupMenu,

        selectedFeature,
        setSelectedFeature,

        onFeatureClick,

        siteViewFeatureGenerationConfig,
        setSiteViewFeatureGenerationConfig,

        getHasDetailedModel,
      }}>
      {children}
    </FeatureInteractionContext.Provider>
  )
}

export const useFeatureInteractionContext =
  (): FeatureInteractionContextType => {
    const context = React.useContext(FeatureInteractionContext)
    if (!context)
      throw new Error(
        'useFeatureInteractionContext() must be used within a FeatureInteractionContextProvider'
      )
    return context
  }
